import { useRouter } from 'next/router'
import { useEffect, useMemo } from 'react'
import {
  OrderFormat,
  OutletState,
  ServiceTimeKindType,
} from '@ancon/wildcat-types'
import getOutletOrderFormats from '@ancon/wildcat-utils/outlet/getOutletOrderFormats'
import {
  getOutletNextReadyToOrderTime,
  isOutletAcceptingPreOrders,
} from '@ancon/wildcat-utils/outlet'
import { batch } from 'react-redux'

import { OrderFormatUrlSlug } from '../../app/constants'
import {
  outletFiltersOrderFormatSelector,
  outletListItemSelector,
} from '../store/outletSelector'
import useAppSelector from '../../../store/hooks/useAppSelector'
import useAppDispatch from '../../../store/hooks/useAppDispatch'
import {
  outletsSetSelectedOutletListItem,
  outletsSetVisibleOrderFormatSelectionModal,
} from '../../outlets/store/outletsSlice'
import { validateTableOrderUrl } from '../store/outletThunks'
import {
  outletSetIsVisibleServiceTimeKindSelectionModal,
  outletSetOutletFilters,
} from '../store/outletSlice'
import localStorageUtils from '../../app/utils/localStorageUtils'
import { LocalStorageCartMeta } from '../../checkout/types'
import useAppStore from '../../../store/hooks/useAppStore'
import { checkoutIsWalletTopUpSelector } from '../../checkout/store/checkoutSelectors'

function isValidServiceTimeParam(serviceTime: string) {
  const date = new Date(serviceTime)
  return !Number.isNaN(date.getTime())
}

export default function TableOrderingFilter() {
  const router = useRouter()
  const store = useAppStore()
  const dispatch = useAppDispatch()
  const outlet = useAppSelector(outletListItemSelector)
  const orderFormats = useMemo(
    () => (outlet ? getOutletOrderFormats(outlet) : []),
    [outlet],
  )

  const isWalletTopUpCheckout = useAppSelector(checkoutIsWalletTopUpSelector)

  const { id: outletId } = outlet || {}
  const {
    orderFormat,
    tableId,
    serviceTime: queryServiceTime,
  } = router?.query || {}
  const hasQueryServiceTime = queryServiceTime !== undefined

  useEffect(() => {
    function isOutletStatusAcceptingPreOrders() {
      const isAcceptingPreOrders = isOutletAcceptingPreOrders(
        outlet!.orderFormatSettings,
      )
      const isOpened = outlet!.state === OutletState.Opened
      const isAcceptingOfflineOrders = !isOpened && isAcceptingPreOrders

      return isAcceptingOfflineOrders
    }

    const checkout = localStorageUtils.getItem<LocalStorageCartMeta>('cartMeta')

    if (checkout?.outletId !== outlet?.id || isWalletTopUpCheckout) {
      const orderFormatNumber = /^[0-9]*$/.test(orderFormat as string)
        ? Number(orderFormat)
        : OrderFormatUrlSlug[orderFormat as keyof typeof OrderFormatUrlSlug] ??
          undefined
      const isAvailableOrderFormat =
        orderFormatNumber && orderFormats.includes(orderFormatNumber)

      if (!orderFormat || !isAvailableOrderFormat) {
        if (orderFormats.length === 1) {
          const isAcceptingOfflineOrders = isOutletStatusAcceptingPreOrders()
          if (isAcceptingOfflineOrders) {
            dispatch(outletSetIsVisibleServiceTimeKindSelectionModal(true))
          }
          dispatch(
            outletSetOutletFilters({
              orderFormat: orderFormats[0],
              ...(isAcceptingOfflineOrders && {
                serviceTime: {
                  kind: ServiceTimeKindType.AtSpecifiedTime,
                  time: getOutletNextReadyToOrderTime(
                    outlet!,
                    orderFormats[0],
                  ).toISOString(),
                },
              }),
            }),
          )
        } else {
          // When go back click after order format selected
          const selectedOrderFormat = outletFiltersOrderFormatSelector(
            store.getState(),
          )

          batch(() => {
            dispatch(outletsSetSelectedOutletListItem(outlet))
            if (!selectedOrderFormat) {
              dispatch(outletsSetVisibleOrderFormatSelectionModal(true))
            }
          })
        }
      } else if (orderFormatNumber === OrderFormat.TableOrder && tableId) {
        dispatch(
          validateTableOrderUrl({
            outletId: outletId as string,
            tableId: tableId as string,
          }),
        )
      } else {
        const isValidServiceTime = isValidServiceTimeParam(
          queryServiceTime as string,
        )

        if (isOutletStatusAcceptingPreOrders() && !queryServiceTime) {
          dispatch(outletSetIsVisibleServiceTimeKindSelectionModal(true))
        }

        dispatch(
          outletSetOutletFilters({
            orderFormat: orderFormatNumber,
            ...(hasQueryServiceTime && {
              serviceTime: {
                kind: queryServiceTime
                  ? ServiceTimeKindType.AtSpecifiedTime
                  : ServiceTimeKindType.AsSoonAsPossible,
                time: isValidServiceTime ? (queryServiceTime as string) : '',
              },
            }),
            ...(isOutletStatusAcceptingPreOrders() && {
              serviceTime: {
                kind: ServiceTimeKindType.AtSpecifiedTime,
                time: isValidServiceTime
                  ? (queryServiceTime as string)
                  : getOutletNextReadyToOrderTime(
                      outlet!,
                      orderFormatNumber,
                    ).toISOString(),
              },
            }),
          }),
        )
      }
    }
  }, [
    orderFormats,
    dispatch,
    orderFormat,
    outlet,
    tableId,
    outletId,
    queryServiceTime,
    hasQueryServiceTime,
    store,
    isWalletTopUpCheckout,
  ])

  return null
}
